import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Buildings, Plus, X, CaretDown, CaretRight } from '@phosphor-icons/react';
import { apiPath } from '../../config/apiBase';
import { notifySuccess, notifyError } from '../../lib/notify';
import ListSearch from '../../components/ListSearch';
import ClientsPanel from './ClientsPanel';

const LOC = apiPath('/api/tickets/data-sources/locations');

function authHeaders() {
  const token = localStorage.getItem('token');
  return { withCredentials: true, headers: { Authorization: `Bearer ${token}` } };
}

const inputStyle = {
  padding: '7px 10px',
  border: '1px solid #D1D5DB',
  borderRadius: 8,
  fontSize: 12,
  background: 'white',
};
const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '7px 12px',
  border: 'none',
  borderRadius: 8,
  background: '#002FA7',
  color: 'white',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
const linkDanger = {
  background: 'none', border: 'none', color: '#B91C1C', fontSize: 12, cursor: 'pointer', padding: 0,
};

/**
 * Product → Clients and branches.
 *
 * Add a client (company) and any number of branches under it for this product.
 * Everything is stored in the product's Companies / Branches data sources (the
 * same lists the ticket form, WhatsApp/email mapping and widgets use), so the
 * Data sources tab shows the changes straight away. Adding a client also
 * creates the matching client account used for team-member client tags.
 */
export default function ClientsBranchesPanel({ productId, productName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [newClient, setNewClient] = useState('');
  const [newGroup, setNewGroup] = useState('');
  const [branchDraft, setBranchDraft] = useState({}); // company → text
  const [open, setOpen] = useState({}); // company → bool
  const [busy, setBusy] = useState(false);
  const [showAccounts, setShowAccounts] = useState(false);

  const url = `${LOC}/${encodeURIComponent(productId)}`;

  const load = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const r = await axios.get(url, authHeaders());
      setData(r.data);
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Failed to load clients');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [productId, url]);

  useEffect(() => { load(); }, [load]);

  const grouped = data?.mode === 'group_company_branch';
  const companies = useMemo(() => {
    const rows = (data?.companies || []).map((c) => c.value || c.label).filter(Boolean);
    const groupOf = {};
    Object.entries(data?.companies_by_group || {}).forEach(([g, opts]) => {
      (opts || []).forEach((o) => { if (o?.value) groupOf[o.value] = g; });
    });
    const q = search.trim().toLowerCase();
    return rows
      .map((name) => ({
        name,
        group: grouped ? groupOf[name] : null,
        branches: (data?.branches_by_company?.[name] || []).map((b) => b.value || b.label).filter(Boolean),
      }))
      .filter((c) => !q || c.name.toLowerCase().includes(q) || c.branches.some((b) => b.toLowerCase().includes(q)))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data, search, grouped]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r?.data) setData(r.data);
      if (okMsg) notifySuccess(okMsg);
      return true;
    } catch (err) {
      notifyError(err.response?.data?.detail || 'Request failed');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const addClient = async (e) => {
    e?.preventDefault();
    const company = newClient.trim();
    if (!company) return;
    const body = { company };
    if (grouped && newGroup) body.group = newGroup;
    const ok = await run(() => axios.post(`${url}/companies`, body, authHeaders()), `Client "${company}" added`);
    if (ok) {
      setNewClient('');
      setOpen((o) => ({ ...o, [company]: true }));
    }
  };

  const removeClient = async (company) => {
    if (!window.confirm(`Remove client "${company}" and all its branches from ${productName || 'this product'}?`)) return;
    await run(
      () => axios.delete(`${url}/companies`, { ...authHeaders(), data: { company } }),
      'Client removed',
    );
  };

  const addBranch = async (company) => {
    const branch = (branchDraft[company] || '').trim();
    if (!branch) return;
    const ok = await run(
      () => axios.post(`${url}/branches`, { company, branch }, authHeaders()),
      `Branch "${branch}" added`,
    );
    if (ok) setBranchDraft((d) => ({ ...d, [company]: '' }));
  };

  const removeBranch = async (company, branch) => {
    if (!window.confirm(`Remove branch "${branch}" from ${company}?`)) return;
    await run(
      () => axios.delete(`${url}/branches`, { ...authHeaders(), data: { company, branch } }),
      'Branch removed',
    );
  };

  const groups = (data?.groups || []).map((g) => g.value || g.label).filter(Boolean);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
        <Buildings size={22} color="#002FA7" />
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Clients and branches</div>
          <div style={{ fontSize: 12, color: '#6B7280', maxWidth: 720 }}>
            Add the clients of <strong>{productName}</strong> and the branches under each one.
            They are saved to this product&apos;s data sources, so ticket forms, WhatsApp / email
            mapping and client widgets pick them up immediately.
          </div>
        </div>
      </div>

      <form onSubmit={addClient} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <input
          value={newClient}
          onChange={(e) => setNewClient(e.target.value)}
          placeholder="New client name…"
          style={{ ...inputStyle, minWidth: 240 }}
          aria-label="New client name"
        />
        {grouped && (
          <select
            value={newGroup}
            onChange={(e) => setNewGroup(e.target.value)}
            style={inputStyle}
            aria-label="Client group"
          >
            <option value="">Group: Others</option>
            {groups.map((g) => <option key={g} value={g}>Group: {g}</option>)}
          </select>
        )}
        <button type="submit" disabled={busy || !newClient.trim()} style={{ ...primaryBtn, opacity: (busy || !newClient.trim()) ? 0.6 : 1 }}>
          <Plus size={14} weight="bold" />
          Add client
        </button>
      </form>

      <ListSearch
        value={search}
        onChange={setSearch}
        placeholder="Search clients or branches…"
        resultCount={companies.length}
        totalCount={(data?.companies || []).length}
        maxWidth="100%"
      />

      {loading ? (
        <p style={{ color: '#9CA3AF', fontSize: 12 }}>Loading clients…</p>
      ) : companies.length === 0 ? (
        <p style={{ color: '#9CA3AF', fontSize: 12 }}>
          {search ? `No clients match "${search}".` : 'No clients yet — add the first one above.'}
        </p>
      ) : (
        <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
          {companies.map((c) => {
            const isOpen = !!open[c.name];
            return (
              <div key={c.name} style={{ borderBottom: '1px solid #F3F4F6' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: isOpen ? '#F9FAFB' : 'white' }}>
                  <button
                    type="button"
                    onClick={() => setOpen((o) => ({ ...o, [c.name]: !isOpen }))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, padding: 0, flex: 1, textAlign: 'left' }}
                    aria-expanded={isOpen}
                  >
                    {isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}
                    <span style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{c.name}</span>
                    {c.group && <span style={{ fontSize: 11, color: '#6B7280' }}>· {c.group}</span>}
                    <span style={{ fontSize: 11, color: '#6B7280' }}>
                      · {c.branches.length} branch{c.branches.length === 1 ? '' : 'es'}
                    </span>
                  </button>
                  <button type="button" onClick={() => removeClient(c.name)} disabled={busy} style={linkDanger}>
                    Remove
                  </button>
                </div>
                {isOpen && (
                  <div style={{ padding: '8px 12px 12px 34px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                      {c.branches.length === 0 && (
                        <span style={{ fontSize: 12, color: '#9CA3AF' }}>No branches yet</span>
                      )}
                      {c.branches.map((b) => (
                        <span
                          key={b}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px',
                            borderRadius: 999, background: '#EFF6FF', color: '#1E3A8A', fontSize: 12,
                          }}
                        >
                          {b}
                          <button
                            type="button"
                            onClick={() => removeBranch(c.name, b)}
                            disabled={busy}
                            aria-label={`Remove branch ${b}`}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'inline-flex', color: '#1E3A8A' }}
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        value={branchDraft[c.name] || ''}
                        onChange={(e) => setBranchDraft((d) => ({ ...d, [c.name]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addBranch(c.name); } }}
                        placeholder="New branch…"
                        style={{ ...inputStyle, minWidth: 200 }}
                        aria-label={`New branch for ${c.name}`}
                      />
                      <button
                        type="button"
                        onClick={() => addBranch(c.name)}
                        disabled={busy || !(branchDraft[c.name] || '').trim()}
                        style={{ ...primaryBtn, opacity: (busy || !(branchDraft[c.name] || '').trim()) ? 0.6 : 1 }}
                      >
                        <Plus size={14} weight="bold" />
                        Add branch
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 24, borderTop: '1px solid #E5E7EB', paddingTop: 12 }}>
        <button
          type="button"
          onClick={() => setShowAccounts((v) => !v)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, padding: 0, fontSize: 13, fontWeight: 600, color: '#374151' }}
          aria-expanded={showAccounts}
        >
          {showAccounts ? <CaretDown size={14} /> : <CaretRight size={14} />}
          Client accounts (team member client tags, assets, data-source sync)
        </button>
        {showAccounts && (
          <div style={{ marginTop: 12 }}>
            <ClientsPanel productId={productId} />
          </div>
        )}
      </div>
    </div>
  );
}
